import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { proposalStatus, TeamAssistV2ProposalStatus } from 'actions/team_assist_v2/lib/team_assist_v2_answer';
import ProposedActionCard from 'components/customer/team_assist/team_assist_proposed_action';
import { extractSchemaProperties } from 'components/customer/team_assist/team_assist_proposed_action_helpers';

/**
 * A proposed action the answer ended on, as V1's card: the model's arguments
 * to review and edit, Execute and Reject while it waits on the agent, then what
 * became of it. A run glados refused for its input, or because the app
 * couldn't take it, says why beneath it. While `canRun` is false the card
 * waits, and an approval whose outcome never arrived stays locked, since glados
 * takes neither Run nor Reject for it.
 */
export default function TeamAssistV2ProposalCard({ block, canRun = true, isLive = false, onReject, onRun }) {
  const { t } = useTranslation();
  const status = proposalStatus(block, { isLive });
  const isUnconfirmed = status === TeamAssistV2ProposalStatus.unconfirmed;
  const { proposal } = block;
  const isPending = status === TeamAssistV2ProposalStatus.pending;
  const issues = issueMessages(t, (isPending && proposal.issues) || [], proposal.inputSchema);
  const refusal = isPending ? refusalMessage(t, proposal.refusal) : null;

  return (
    <>
      <ProposedActionCard
        execution={cardExecution(block.execution)}
        isDisabled={isUnconfirmed || (isPending && !canRun)}
        isExecuting={status === TeamAssistV2ProposalStatus.running}
        isRejected={status === TeamAssistV2ProposalStatus.rejected}
        onExecute={onRun}
        onReject={onReject}
        proposedAction={{
          toolName: block.name,
          toolDisplayName: proposal.displayName,
          summary: proposal.summary,
          // What ran, once the agent ran it: their edits, not the model's first guess.
          arguments: proposal.approvedInput || block.input,
          inputSchema: proposal.inputSchema,
        }}
      />
      {isUnconfirmed && (
        <RefusalNote data-aid="teamAssistV2ProposalUnconfirmed" role="alert">
          {t("We couldn't confirm whether this action ran. Check the app to see if it did.")}
        </RefusalNote>
      )}
      {refusal && (
        <RefusalNote data-aid="teamAssistV2ProposalRefusal" role="alert">
          {refusal}
        </RefusalNote>
      )}
      {issues.length > 0 && (
        <RefusalNote data-aid="teamAssistV2ProposalIssues" role="alert">
          {t("This action couldn't run. Fix these fields and try again.")}
          <ul>
            {issues.map(message => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        </RefusalNote>
      )}
    </>
  );
}

TeamAssistV2ProposalCard.propTypes = {
  block: PropTypes.shape({
    execution: PropTypes.object,
    input: PropTypes.any,
    name: PropTypes.string,
    proposal: PropTypes.shape({
      approvedInput: PropTypes.any,
      displayName: PropTypes.string,
      inputSchema: PropTypes.object,
      issues: PropTypes.arrayOf(
        PropTypes.shape({ keyword: PropTypes.string, message: PropTypes.string, path: PropTypes.string })
      ),
      refusal: PropTypes.string,
      summary: PropTypes.string,
    }).isRequired,
  }).isRequired,
  /** Whether a pending action can run now: nothing streams, loads, or clears. */
  canRun: PropTypes.bool,
  /** Whether the turn holding the card streams, so an approved action is running. */
  isLive: PropTypes.bool,
  onReject: PropTypes.func.isRequired,
  onRun: PropTypes.func.isRequired,
};

function refusalMessage(t, refusal) {
  switch (refusal) {
    case 'action_unavailable':
      return t("This action isn't available anymore, so nothing ran. Ask Team Assist what to do next.");
    case 'apps_unavailable':
      return t("We couldn't reach your connected apps, so nothing ran. Try again in a moment.");
    default:
      return null;
  }
}

// What glados found wrong with the input, each once, by the field's label in
// the agent's language. glados's own message is Ajv's English, so it isn't shown.
function issueMessages(t, issues, inputSchema) {
  const nodes = extractSchemaProperties(inputSchema);
  return [...new Set(issues.map(issue => issueMessage(t, issue.keyword, fieldLabel(nodes, issue.path))))];
}

function issueMessage(t, keyword, field) {
  if (!field) {
    return t("Something in this action's input isn't valid.");
  }
  switch (keyword) {
    case 'required':
      return t('{{field}} is required.', { field });
    case 'enum':
      return t('Choose one of the listed values for {{field}}.', { field });
    default:
      return t('Check the value in {{field}}.', { field });
  }
}

// The label of the field a JSON pointer such as `/lineItems/0/quantity`
// names, or of the nearest field above it the form shows; null for the whole
// input. An array's row index picks its item.
function fieldLabel(nodes, path) {
  const segments = (path || '')
    .split('/')
    .slice(1)
    .map(segment => segment.replace(/~1/g, '/').replace(/~0/g, '~'));
  let node = null;
  let label = null;
  let children = nodes;
  for (const segment of segments) {
    // A row keeps its array's label: the item schema rarely has its own title.
    if (node && node.kind === 'array' && /^\d+$/.test(segment)) {
      node = node.item;
    } else {
      const next = children.find(child => child.name === segment);
      if (!next) {
        break;
      }
      node = next;
      label = next.label;
    }
    children = node.kind === 'object' ? node.children : [];
  }
  return label;
}

// The card shows a failed run's text as its error details. glados's result
// for a failed run is `{ outcome: 'failed', message }`, the message written
// for the agent; a successful one is the card's own "Executed".
function cardExecution(execution) {
  if (!execution) {
    return undefined;
  }
  const message = execution.isError && execution.result ? execution.result.message : null;
  return { isError: execution.isError, result: typeof message === 'string' ? message : '' };
}

const RefusalNote = styled.div`
  color: ${p => p.theme.colors.red400};
  font-size: var(--font-size-reduced);
  margin-top: 4px;

  ul {
    margin: 4px 0 0;
    padding-left: 16px;
  }
`;
